/**
 * Staff data models for the Klokify application
 * Mirrors the Swift data structures from the iOS app
 */

// Work schedule day structure
// NOTE: Still used by department default schedule feature
// (StaffContext.fetchDepartmentDefaultSchedule) - unrelated to the
// removed staff-level Contract Hours / Weekly Work Schedule feature.
export class WorkScheduleDay {
  constructor(isWorkDay = false, hours = 0) {
    this.isWorkDay = isWorkDay;
    this.hours = hours;
  }

  // Create from Firestore data
  static fromFirestore(data) {
    if (!data) return new WorkScheduleDay();
    return new WorkScheduleDay(
      data.isWorkDay || false,
      data.hours || 0
    );
  }

  // Convert to Firestore data
  toFirestore() {
    return {
      isWorkDay: this.isWorkDay,
      hours: this.hours
    };
  }
}

// Staff employment information
export class StaffEmploymentInfo {
  constructor(
    startDate = new Date(),
    contractHours = null,
    vacationDays = {},
    workSchedule = null
  ) {
    this.startDate = startDate;
    this.vacationDays = vacationDays;

    // LEGACY FIELDS - contractHours / workSchedule
    // These are no longer collected in the UI and no longer written to
    // Firestore (see toFirestore() below). Schedule Planner shifts are
    // the single source of truth for the Hours Account target, sick
    // leave, and vacation calculations. Retained here only so that
    // fromFirestore() can tolerate pre-existing documents that still
    // contain these fields without throwing - they are not read by
    // any calculation anywhere in the app.
    this.contractHours = contractHours;
    this.workSchedule = workSchedule;
  }

  // Create from Firestore data
  static fromFirestore(data) {
    if (!data) {
      return new StaffEmploymentInfo();
    }

    // Parse start date
    const startDate = data.startDate
      ? (data.startDate instanceof Date ? data.startDate : data.startDate.toDate())
      : new Date();

    // Parse vacation days
    const vacationDays = data.vacationDays || {};

    // Tolerate legacy fields if present on old documents - not used
    // in any calculation, kept only so reads never throw.
    const contractHours = data.contractHours !== undefined ? data.contractHours : null;
    const workSchedule = data.workSchedule || null;

    return new StaffEmploymentInfo(
      startDate,
      contractHours,
      vacationDays,
      workSchedule
    );
  }

  // Convert to Firestore data
  // NOTE: contractHours / workSchedule are intentionally omitted here.
  // Schedule Planner shifts are the source of truth for hours
  // calculations - writing these legacy fields is no longer needed.
  toFirestore() {
    return {
      startDate: this.startDate,
      vacationDays: this.vacationDays
    };
  }
}

// Schedule exception for staff member
export class ScheduleException {
  constructor(
    id = null,
    date = new Date(),
    type = 'off', // 'off', 'vacation', 'custom'
    hours = 0,
    description = ''
  ) {
    this.id = id || Date.now().toString();
    this.date = date;
    this.type = type;
    this.hours = hours;
    this.description = description;
  }

  // Create from Firestore data
  static fromFirestore(data, id = null) {
    if (!data) return null;
    
    return new ScheduleException(
      id || data.id,
      data.date ? (data.date instanceof Date ? data.date : data.date.toDate()) : new Date(),
      data.type || 'off',
      data.hours || 0,
      data.description || ''
    );
  }

  // Convert to Firestore data
  toFirestore() {
    return {
      date: this.date,
      type: this.type,
      hours: this.hours,
      description: this.description
    };
  }

  // Get date as string in YYYY-MM-DD format
  get dateString() {
    return this.date.toISOString().split('T')[0];
  }
}

// Active session model for currently clocked in staff
export class ActiveSession {
  constructor(
    staffId = '',
    departmentId = '',
    clockIn = new Date(),
    onBreak = false,
    breakStart = null,
    deviceType = 'Web',
    deviceId = 'admin',
    location = { lat: 0, lon: 0 }
  ) {
    this.staffId = staffId;
    this.departmentId = departmentId;
    this.clockIn = clockIn;
    this.onBreak = onBreak;
    this.breakStart = breakStart;
    this.deviceType = deviceType;
    this.deviceId = deviceId;
    this.location = location;
    
    // Computed properties for display
    this.staffName = '';
    this.departmentName = '';
  }

  // Create from Firestore data
  static fromFirestore(data, staffId) {
    if (!data) return null;
    
    return new ActiveSession(
      staffId,
      data.departmentId || '',
      data.clockIn ? (data.clockIn instanceof Date ? data.clockIn : data.clockIn.toDate()) : new Date(),
      data.onBreak || false,
      data.breakStart ? (data.breakStart instanceof Date ? data.breakStart : data.breakStart.toDate()) : null,
      data.deviceType || 'iOS',
      data.deviceId || 'unknown',
      data.location || { lat: 0, lon: 0 }
    );
  }

  // Convert to Firestore data
  toFirestore() {
    const data = {
      departmentId: this.departmentId,
      clockIn: this.clockIn,
      onBreak: this.onBreak,
      deviceType: this.deviceType,
      deviceId: this.deviceId,
      location: this.location
    };

    if (this.breakStart) {
      data.breakStart = this.breakStart;
    }

    return data;
  }

  // Calculate current session duration
  getCurrentDuration() {
    const now = new Date();
    return Math.floor((now - this.clockIn) / 1000); // Duration in seconds
  }

  // Calculate current break duration if on break
  getCurrentBreakDuration() {
    if (!this.onBreak || !this.breakStart) return 0;
    const now = new Date();
    return Math.floor((now - this.breakStart) / 1000); // Duration in seconds
  }
}

// Session status enum
export const SessionStatus = {
  NOT_CLOCKED_IN: 'Not Clocked In',
  CLOCKED_IN: 'Clocked In',
  WORKING: 'Working',
  ON_BREAK: 'On Break',
  CLOCKED_OUT: 'Clocked Out'
};

// Time entry model for historical time tracking data
export class TimeEntry {
  constructor(
    id = null,
    departmentId = '',
    date = '',
    clockIns = [],
    clockOuts = [],
    breakStarts = [],
    breakEnds = [],
    totalWorkDuration = 0,
    totalBreakDuration = 0,
    sessionStatus = SessionStatus.NOT_CLOCKED_IN,
    deviceType = 'Web',
    deviceId = 'admin',
    note = ''
  ) {
    this.id = id;
    this.departmentId = departmentId;
    this.date = date;
    this.clockIns = clockIns;
    this.clockOuts = clockOuts;
    this.breakStarts = breakStarts;
    this.breakEnds = breakEnds;
    this.totalWorkDuration = totalWorkDuration;
    this.totalBreakDuration = totalBreakDuration;
    this.sessionStatus = sessionStatus;
    this.deviceType = deviceType;
    this.deviceId = deviceId;
    this.note = note;

    // Computed properties for display
    this.departmentName = '';
  }

  // Create from Firestore data
  static fromFirestore(doc) {
    if (!doc || !doc.exists) return null;
    
    const data = doc.data();
    
    // Convert Firestore timestamps to Date objects
    const clockIns = (data.clockIns || []).map(ts => 
      ts instanceof Date ? ts : ts.toDate()
    );
    const clockOuts = (data.clockOuts || []).map(ts => 
      ts instanceof Date ? ts : ts.toDate()
    );
    const breakStarts = (data.breakStarts || []).map(ts => 
      ts instanceof Date ? ts : ts.toDate()
    );
    const breakEnds = (data.breakEnds || []).map(ts => 
      ts instanceof Date ? ts : ts.toDate()
    );

    return new TimeEntry(
      doc.id,
      data.departmentId || '',
      data.date || '',
      clockIns,
      clockOuts,
      breakStarts,
      breakEnds,
      data.totalWorkDuration || 0,
      data.totalBreakDuration || 0,
      data.sessionStatus || SessionStatus.NOT_CLOCKED_IN,
      data.deviceType || 'iOS',
      data.deviceId || 'unknown',
      data.note || ''
    );
  }

  // Convert to Firestore data
  toFirestore() {
    return {
      departmentId: this.departmentId,
      date: this.date,
      clockIns: this.clockIns,
      clockOuts: this.clockOuts,
      breakStarts: this.breakStarts,
      breakEnds: this.breakEnds,
      totalWorkDuration: this.totalWorkDuration,
      totalBreakDuration: this.totalBreakDuration,
      sessionStatus: this.sessionStatus,
      deviceType: this.deviceType,
      deviceId: this.deviceId,
      note: this.note
    };
  }

  // Get formatted date for display
  getFormattedDate() {
    const date = new Date(this.date);
    return date.toLocaleDateString();
  }

  // Check if entry has active session (more clock ins than clock outs)
  hasActiveSession() {
    return this.clockIns.length > this.clockOuts.length;
  }

  // Get last clock in time
  getLastClockIn() {
    return this.clockIns.length > 0 ? this.clockIns[this.clockIns.length - 1] : null;
  }

  // Get last clock out time
  getLastClockOut() {
    return this.clockOuts.length > 0 ? this.clockOuts[this.clockOuts.length - 1] : null;
  }
}

// Time adjustment tracking model
export class TimeAdjustment {
  constructor(
    id = null,
    staffId = '',
    entryId = '',
    adjustmentType = 'manual_edit', // 'manual_edit', 'admin_clockout', 'correction'
    originalTimestamps = {},
    adjustedTimestamps = {},
    reason = '',
    adjustedBy = 'admin',
    adjustmentDate = new Date()
  ) {
    this.id = id || Date.now().toString();
    this.staffId = staffId;
    this.entryId = entryId;
    this.adjustmentType = adjustmentType;
    this.originalTimestamps = originalTimestamps;
    this.adjustedTimestamps = adjustedTimestamps;
    this.reason = reason;
    this.adjustedBy = adjustedBy;
    this.adjustmentDate = adjustmentDate;
  }

  // Create from Firestore data
  static fromFirestore(doc) {
    if (!doc || !doc.exists) return null;
    
    const data = doc.data();
    
    return new TimeAdjustment(
      doc.id,
      data.staffId || '',
      data.entryId || '',
      data.adjustmentType || 'manual_edit',
      data.originalTimestamps || {},
      data.adjustedTimestamps || {},
      data.reason || '',
      data.adjustedBy || 'admin',
      data.adjustmentDate ? (data.adjustmentDate instanceof Date ? data.adjustmentDate : data.adjustmentDate.toDate()) : new Date()
    );
  }

  // Convert to Firestore data
  toFirestore() {
    return {
      staffId: this.staffId,
      entryId: this.entryId,
      adjustmentType: this.adjustmentType,
      originalTimestamps: this.originalTimestamps,
      adjustedTimestamps: this.adjustedTimestamps,
      reason: this.reason,
      adjustedBy: this.adjustedBy,
      adjustmentDate: this.adjustmentDate
    };
  }
}

// Complete staff member data structure
export class Staff {
  constructor(
    id = null,
    name = '',
    pin = '0000',
    departmentIds = [],
    primaryDepartmentId = '',
    email = '',
    role = 'staff',
    employmentInfo = null,
    active = true,
    archivedAt = null,
    archivedBy = null
  ) {
    this.id = id;
    this.name = name;
    this.pin = pin;
    this.departmentIds = departmentIds;
    this.primaryDepartmentId = primaryDepartmentId;
    this.email = email;
    this.role = role;
    this.employmentInfo = employmentInfo || new StaffEmploymentInfo();

    // Archive state.
    // NOTE: `active` must default to `true` and must be treated as active
    // for any existing document that lacks the field entirely - never
    // query Firestore with `where('active', '==', true)`, filter in memory
    // with `s.active !== false` instead (see StaffContext.activeStaff).
    this.active = active;
    this.archivedAt = archivedAt;
    this.archivedBy = archivedBy;
    
    // Add properties for department names
    this.departmentNames = [];  // Array of department names
    this.primaryDepartmentName = 'Unassigned';  // Name of primary department
  }

  // Create from Firestore data
  static fromFirestore(doc) {
    if (!doc || !doc.exists) return null;
    
    const data = doc.data();
    const employmentInfoData = data.employmentInfo || {};

    // A document with no `active` field is treated as active (legacy staff
    // created before this field existed). Only an explicit `false` counts
    // as archived.
    const active = data.active !== false;

    const archivedAt = data.archivedAt
      ? (data.archivedAt instanceof Date ? data.archivedAt : data.archivedAt.toDate())
      : null;

    const archivedBy = data.archivedBy || null;
    
    return new Staff(
      doc.id,
      data.name || 'Unknown',
      data.pin || '0000',
      data.departmentIds || [],
      data.primaryDepartmentId || '',
      data.email || '',
      data.role || 'staff',
      StaffEmploymentInfo.fromFirestore(employmentInfoData),
      active,
      archivedAt,
      archivedBy
    );
  }

  // Convert to Firestore data
  //
  // NOTE: `archivedAt` / `archivedBy` are intentionally NOT written here.
  // Those fields are set exclusively via StaffContext.archiveStaffMember /
  // reactivateStaffMember using targeted updateDoc() calls (with
  // serverTimestamp() / deleteField()). Including them in this generic
  // toFirestore() would risk an unrelated save (e.g. from StaffForm)
  // silently clobbering archive metadata on an already-archived record.
  toFirestore() {
    return {
      name: this.name,
      pin: this.pin,
      departmentIds: this.departmentIds,
      primaryDepartmentId: this.primaryDepartmentId,
      email: this.email,
      role: this.role,
      employmentInfo: this.employmentInfo.toFirestore(),
      active: this.active
    };
  }

  // True once a staff member has been archived (active explicitly false).
  get isArchived() {
    return this.active === false;
  }
  
  // Set department names based on departments data
  setDepartmentNames(departments) {
    if (!departments || !Array.isArray(departments)) {
      return;
    }
    
    // Map department IDs to names
    this.departmentNames = this.departmentIds.map(id => {
      const dept = departments.find(d => d.id === id);
      return dept ? dept.name : 'Unknown Department';
    });
    
    // Set primary department name
    if (this.primaryDepartmentId) {
      const primaryDept = departments.find(d => d.id === this.primaryDepartmentId);
      this.primaryDepartmentName = primaryDept ? primaryDept.name : 'Unassigned';
    } else {
      this.primaryDepartmentName = 'Unassigned';
    }
  }
}